"use client";
import { notBlankTextRequirements } from "@/app/(core)/register/types";
import { handleResponseError } from "@/lib/strapi/strapiApi";
import {
  Button,
  MantineTheme,
  Stack,
  Text,
  TextInput,
  Textarea,
  useMantineTheme,
} from "@mantine/core";
import { useForm } from "@mantine/form";
import { modals } from "@mantine/modals";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import { useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { setNotification } from "../../notifications";
export default function ContactForm({
  formId,
  userName,
  userEmail,
}: {
  formId: string;
  userName: string | undefined;
  userEmail: string | undefined;
}) {
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const theme = useMantineTheme();
  const form = useForm({
    initialValues: {
      name: userName ?? "",
      email: userEmail ?? "",
      message: "",
    },
    validate: (values) => {
      return {
        name: notBlankTextRequirements.test(values.name)
          ? null
          : "Enter a valid name",
        email:
          /(?:[a-z0-9!#$%&'*+/=?^_`{|}~-]+(?:\.[a-z0-9!#$%&'*+/=?^_`{|}~-]+)*|"(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21\x23-\x5b\x5d-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])*")@(?:(?:[a-z0-9](?:[a-z0-9-]*[a-z0-9])?\.)+[a-z0-9](?:[a-z0-9-]*[a-z0-9])?|\[(?:(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9]))\.){3}(?:(2(5[0-5]|[0-4][0-9])|1[0-9][0-9]|[1-9]?[0-9])|[a-z0-9-]*[a-z0-9]:(?:[\x01-\x08\x0b\x0c\x0e-\x1f\x21-\x5a\x53-\x7f]|\\[\x01-\x09\x0b\x0c\x0e-\x7f])+)\])/.test(
            values.email,
          )
            ? null
            : "Invalid email",
        message: notBlankTextRequirements.test(values.message)
          ? null
          : "Enter a valid message",
      };
    },
    validateInputOnChange: ["name", "email", "message"],
  });
  async function sendEmail() {
    setLoading(true);
    // get capatcha key
    setNotification(
      "Contact",
      false,
      "Checking you're not a robot...",
      "success",
      true,
    );
    if (!executeRecaptcha) {
      setLoading(false);
      setNotification(
        "Contact",
        false,
        "Our system was not able to verify your human status. Please check you are connected to the internet. If so, try again now!",
        "error",
      );
      return;
    }

    const captchaToken = await executeRecaptcha("ContactFormSubmit");
    const res = await fetch("/api/strapi/sendCustomerEmail", {
      method: "POST",
      body: JSON.stringify({ ...form.values, captchaToken }),
    });
    const response = await res.json();
    if (!res.ok) {
      setNotification("Contact", false, handleResponseError(response), "error");
      setLoading(false);
      return;
    }
    setNotification("Contact", true, response.msg, "success");
    setLoading(false);
    setSent(true);
    setTimeout(() => {
      modals.close(formId);
    }, 1200);
  }
  return sent ? (
    <FormCompleteMsg theme={theme} />
  ) : (
    <Stack>
      <TextInput
        withAsterisk
        label="Name"
        placeholder="Name"
        {...form.getInputProps("name")}
      />
      <TextInput
        withAsterisk
        label="Email"
        placeholder="Where we should reply to your message"
        {...form.getInputProps("email")}
      />
      <Textarea
        autosize
        minRows={4}
        maxRows={10}
        withAsterisk
        label="Message"
        placeholder="Your message for us!"
        {...form.getInputProps("message")}
      />
      <Button
        name="Send"
        color="darkblue"
        mt={"3rem"}
        loading={loading}
        disabled={!form.isValid()}
        ml={"auto"}
        mr={0}
        onClick={() => sendEmail()}
      >
        Send
      </Button>
    </Stack>
  );
}

export function FormCompleteMsg({ theme }: { theme: MantineTheme }) {
  return (
    <Stack align="center" gap={0}>
      <CheckCircleRounded
        style={{ width: 60 }}
        htmlColor={theme.colors.correct[6]}
      />
      <Text color="correct" fw={550} size={"md"}>
        Email sent!
      </Text>
    </Stack>
  );
}
